import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowRight, Check, ShieldCheck, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { useSubmitReview } from '@/hooks/useCustomerReviews';
import { useTenantOrgId } from '@/hooks/useTenantOrgId';
import { cn, renderDisplayText } from '@/lib/utils';

/** Anchor the homepage "Leave a review" link jumps to when the block is on the page. */
export const REVIEW_FORM_ANCHOR = 'leave-review';

export interface ReviewFormContent {
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  buttonText?: string;
}

interface ReviewFormSectionProps {
  content: ReviewFormContent;
  /** default (split card) · centered · alt-bg (split on a tinted band) */
  variant?: string;
  layoutStyle?: string;
  /** Page-builder preview: render the real form but never submit. */
  preview?: boolean;
}

const reviewSchema = z.object({
  name: z.string().trim().min(2, 'Please add your name'),
  location: z.string().trim().max(80, 'Keep the location short').optional(),
  content: z
    .string()
    .trim()
    .min(10, 'Tell us a little more (10 characters or more)')
    .max(1500, 'Reviews can be up to 1,500 characters'),
  rating: z.number().int().min(1).max(5),
});

type ReviewFormValues = z.infer<typeof reviewSchema>;

const RATING_LABELS = ['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent'];

function RatingInput({ value, onChange, disabled }: { value: number; onChange: (rating: number) => void; disabled?: boolean }) {
  const [hovered, setHovered] = useState(0);
  const shown = hovered || value;

  return (
    <div className="flex items-center gap-3">
      <div role="radiogroup" aria-label="Rating" className="tp-rating-input flex items-center" onMouseLeave={() => setHovered(0)}>
        {[1, 2, 3, 4, 5].map((rating) => {
          const on = rating <= shown;
          return (
            <button
              key={rating}
              type="button"
              role="radio"
              aria-checked={value === rating}
              aria-label={`${rating} star${rating === 1 ? '' : 's'}`}
              disabled={disabled}
              onClick={() => onChange(rating)}
              onMouseEnter={() => setHovered(rating)}
              onFocus={() => setHovered(rating)}
              onBlur={() => setHovered(0)}
              className="tp-rating-star rounded-full p-1 text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed"
              data-on={on ? 'true' : 'false'}
            >
              <Star className="h-7 w-7" strokeWidth={on ? 0 : 1.5} fill={on ? 'currentColor' : 'none'} />
            </button>
          );
        })}
      </div>
      <span className="min-w-[5.5rem] text-sm font-medium text-muted-foreground" aria-live="polite">
        {RATING_LABELS[shown]}
      </span>
    </div>
  );
}

/**
 * "Leave a review" page-builder block. Submits through the shared
 * useSubmitReview mutation, so every review lands unapproved in
 * customer_reviews and only appears publicly after the owner approves it in
 * Admin → Reviews.
 */
export function ReviewFormSection({ content, variant = 'default', preview = false }: ReviewFormSectionProps) {
  const orgId = useTenantOrgId();
  const { toast } = useToast();
  const submitReview = useSubmitReview();
  const [submitted, setSubmitted] = useState(false);

  const {
    eyebrow = 'Reviews',
    title = 'Leave a review',
    subtitle = 'Had a clean with us recently? We would love to hear how it went.',
    buttonText = 'Submit review',
  } = content;

  const form = useForm<ReviewFormValues>({
    resolver: zodResolver(reviewSchema),
    defaultValues: { name: '', location: '', content: '', rating: 5 },
  });

  const onSubmit = async (values: ReviewFormValues) => {
    if (preview) {
      toast({ title: 'Preview only', description: 'The form goes live on your published page.' });
      return;
    }
    if (!orgId) return;

    try {
      await submitReview.mutateAsync({
        organization_id: orgId,
        customer_name: values.name.trim(),
        customer_location: values.location?.trim() || null,
        content: values.content.trim(),
        rating: values.rating,
      });
      form.reset();
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting review:', error);
      toast({ title: 'Unable to submit review', description: 'Please try again in a moment.', variant: 'destructive' });
    }
  };

  const centered = variant === 'centered';
  const busy = submitReview.isPending;

  const intro = (
    <div className={cn(centered ? 'mx-auto max-w-xl text-center' : 'lg:col-span-5')}>
      {eyebrow && <p className="tp-eyebrow" data-reveal>{eyebrow}</p>}
      <h2 className="tp-h2 mt-5" data-reveal style={{ ['--reveal-delay' as string]: '70ms' }}>
        {renderDisplayText(title)}
      </h2>
      {subtitle && (
        <p className="tp-lead mt-5" data-reveal style={{ ['--reveal-delay' as string]: '140ms' }}>
          {subtitle}
        </p>
      )}
      <p
        className={cn('mt-7 flex items-center gap-2.5 text-sm text-muted-foreground', centered && 'justify-center')}
        data-reveal
        style={{ ['--reveal-delay' as string]: '200ms' }}
      >
        <ShieldCheck className="h-4 w-4 shrink-0 text-primary" />
        Reviews are checked before they appear publicly.
      </p>
    </div>
  );

  const card = (
    <div
      className={cn('tp-card p-6 sm:p-8 md:p-10', centered ? 'mx-auto mt-12 max-w-2xl' : 'lg:col-span-7')}
      data-reveal
      style={{ ['--reveal-delay' as string]: '120ms' }}
    >
      {submitted ? (
        <div className="tp-review-success flex min-h-[22rem] flex-col items-center justify-center text-center" role="status">
          <span className="tp-review-success-mark flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <Check className="h-8 w-8" strokeWidth={2.5} />
          </span>
          <h3 className="tp-h3 mt-7">Thank you for your review</h3>
          <p className="mt-3 max-w-sm text-muted-foreground">
            It has been sent to the team and will appear on the site once it has been approved.
          </p>
          <button type="button" className="tp-link mt-8 text-sm" onClick={() => setSubmitted(false)}>
            Write another review <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
            <fieldset disabled={busy} className="space-y-5">
              <FormField
                control={form.control}
                name="rating"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Your rating</FormLabel>
                    <RatingInput value={field.value} onChange={field.onChange} disabled={busy} />
                  </FormItem>
                )}
              />
              <div className="grid gap-5 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input className="h-12" autoComplete="name" placeholder="e.g. Sarah J." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="location"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Location <span className="font-normal text-muted-foreground">(optional)</span>
                      </FormLabel>
                      <FormControl>
                        <Input className="h-12" autoComplete="address-level2" placeholder="Town or area" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="content"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Review</FormLabel>
                    <FormControl>
                      <Textarea className="min-h-[140px] resize-y" placeholder="What did you think of your clean?" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex flex-col-reverse gap-4 pt-1 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-muted-foreground">
                  {preview ? 'Preview — submissions are switched off in the builder.' : 'We only show your name and location.'}
                </p>
                <Button type="submit" variant="hero" size="lg" className="tp-btn group h-12 px-7 hover:translate-y-0">
                  {busy ? 'Sending…' : buttonText || 'Submit review'}
                  <span className="tp-btn-arrow"><ArrowRight /></span>
                </Button>
              </div>
            </fieldset>
          </form>
        </Form>
      )}
    </div>
  );

  return (
    <section
      id={REVIEW_FORM_ANCHOR}
      className={cn('tp-section scroll-mt-28', variant === 'alt-bg' && 'tp-tint')}
      data-section="review_form"
    >
      <div className="container mx-auto px-5 md:px-6">
        {centered ? (
          <>
            {intro}
            {card}
          </>
        ) : (
          <div className="grid items-start gap-10 lg:grid-cols-12 lg:gap-14">
            {intro}
            {card}
          </div>
        )}
      </div>
    </section>
  );
}
